import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { tariffsApi } from '../client';
import { fetchUserBalance, updateUserBalance } from '../services/balances';
import { fetchUserTariff, upsertUserTariff } from '../services/user-tariffs';
import type { UserTariffRow } from '../types';

function toRow(item: {
  user_id: number;
  balance?: number | null;
  currency?: string | null;
  tariff_id?: number | null;
  billing_type?: number | null;
}): UserTariffRow {
  return {
    id: item.user_id,
    user_id: item.user_id,
    balance: item.balance ?? null,
    currency: item.currency ?? null,
    tariff_id: item.tariff_id ?? null,
    billing_type: item.billing_type ?? null,
  };
}

export const usersAdapter: CrudAdapter<UserTariffRow, never, UserTariffRow> = {
  async list({ offset, limit, sortName, sortDirection }) {
    const { data, error } = await tariffsApi.GET('/users/', {
      params: {
        query: {
          offset,
          limit,
          'sort.name': sortName ?? 'user_id',
          'sort.direction': (sortDirection ?? 'desc') as 'asc' | 'desc',
        },
      },
    });
    if (error) throw error;
    return {
      items: (data?.items ?? []).map(toRow),
      total: data?.total ?? 0,
    };
  },

  async byKey(id) {
    const userId = Number(id);
    const [tariff, balance] = await Promise.all([
      fetchUserTariff(userId),
      fetchUserBalance(userId).catch(() => null),
    ]);
    return toRow({
      user_id: userId,
      balance: balance?.balance ?? null,
      currency: balance?.currency ?? null,
      tariff_id: tariff?.tariff_id ?? null,
      billing_type: tariff?.billing_type ?? null,
    });
  },

  async update(payload) {
    const userId = payload.user_id ?? Number(payload.id);
    const currency = (payload.currency ?? '').trim() || null;
    let balance: number | null = null;
    if (typeof payload.balance === 'number' && Number.isFinite(payload.balance)) {
      balance = payload.balance;
    } else if (payload.balance != null) {
      const n = Number(payload.balance);
      if (Number.isFinite(n)) balance = n;
    }

    // Currency + amount → PUT /balances/; tariff fields → PUT /users/tariffs.
    if (currency) {
      if (balance == null) {
        const current = await fetchUserBalance(userId).catch(() => null);
        balance = current?.balance ?? 0;
      }
      await updateUserBalance({
        user_id: userId,
        amount: balance,
        currency,
      });
      const fresh = await fetchUserBalance(userId).catch(() => null);
      if (fresh) balance = fresh.balance;
    }

    const updated = await upsertUserTariff({
      user_id: userId,
      tariff_id: payload.tariff_id ?? null,
      billing_type: payload.billing_type ?? null,
    });

    return toRow({
      user_id: userId,
      balance,
      currency,
      tariff_id: updated.tariff_id,
      billing_type: updated.billing_type,
    });
  },
};

export const usersStore = useCrudDataSource(usersAdapter);
